import { supabase } from '../lib/supabase';

/**
 * ---------------------------------------------------------------------------
 * Signed reads for PRIVATE storage buckets
 * ---------------------------------------------------------------------------
 * Driver documents, invoices and photos live in private buckets. The
 * `/object/public/` route only answers for a public bucket, so every stored
 * link has to be exchanged for a short-lived signed one before it goes into
 * an href or a src.
 *
 * Existing rows still hold the old public URL as plain text (getPublicUrl is
 * pure string building, so uploads keep writing that shape). The parser
 * accepts what the columns actually hold:
 *
 *   https://<ref>.supabase.co/storage/v1/object/public/<bucket>/<path>
 *   https://<ref>.supabase.co/storage/v1/object/sign/<bucket>/<path>?token=…
 *   <path>   — a bare path, ONLY when the caller says which bucket it is in
 *
 * Bare paths never name their bucket here: company-documents has folders
 * called `driver-documents/`, `load-documents/`, `invoices/` … and a
 * `driver-documents` bucket may also exist. Guessing the bucket from the
 * first folder would sign those paths against the wrong bucket.
 *
 * Adapted from STARBG (tmsstarbg src/utils/fileUrls.ts).
 */

const PUBLIC_SEGMENT = '/storage/v1/object/public/';
const SIGN_SEGMENT = '/storage/v1/object/sign/';

/**
 * Every bucket a stored URL may point at. A URL naming any other bucket is
 * left untouched (and warned about once), because signing it would fail with
 * an error indistinguishable from a deleted file.
 *
 * Keep in step with the bucket lists in
 * supabase/migrations/*_private_bucket_storage_policies.sql.
 */
const KNOWN_BUCKETS = new Set([
  'company-documents',
  'logos',
  'maintenance-documents',
  'documents',
  'driver-documents',
  'eld-documents',
]);

/**
 * Hosts whose storage URLs belong to this project. Old rows can point at
 * another Supabase project (a residual ref is hardcoded in a few services);
 * those are not ours to sign, so they pass through unchanged instead of
 * turning into "Unavailable".
 */
const OWN_HOSTS = new Set<string>();
try {
  OWN_HOSTS.add(new URL(import.meta.env.VITE_SUPABASE_URL).host);
} catch {
  // lib/supabase.ts already throws on a missing URL
}

export interface StorageRef {
  bucket: string;
  path: string;
}

export interface StorageOptions {
  /** Bucket for a bare path. Without it a bare path is not treated as ours. */
  bucket?: string;
}

/**
 * Logs go to Sentry (captureConsoleIntegration turns console.warn/error into
 * events), and stored paths carry driver names. So: never log the value, and
 * log each distinct problem once per page load instead of once per file.
 */
const reported = new Set<string>();
function reportOnce(key: string, message: string): void {
  if (reported.has(key)) return;
  reported.add(key);
  console.warn(message);
}

/**
 * Pull bucket + path out of a stored value. Returns null for anything that is
 * not one of ours — an external link, an empty cell, a data: or blob: URI —
 * so callers can pass those through untouched.
 */
export function parseStorageRef(
  stored: string | null | undefined,
  opts: StorageOptions = {}
): StorageRef | null {
  const raw = String(stored || '').trim();
  if (!raw) return null;

  // A multi-file column holds a JSON array string; callers must split it first.
  if (raw.startsWith('[')) return null;

  // A scheme other than http(s) (data:, blob:, mailto:) is never ours, even if
  // its text happens to contain a storage path.
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^https?:\/\//i.test(raw)) return null;

  const segment = raw.includes(PUBLIC_SEGMENT)
    ? PUBLIC_SEGMENT
    : raw.includes(SIGN_SEGMENT)
      ? SIGN_SEGMENT
      : null;

  if (segment) {
    if (/^https?:\/\//i.test(raw)) {
      let host = '';
      try {
        host = new URL(raw).host;
      } catch {
        return null;
      }
      if (!OWN_HOSTS.has(host)) return null;
    }

    const after = raw.slice(raw.indexOf(segment) + segment.length);
    const q = after.indexOf('?');
    const bucketAndPath = q === -1 ? after : after.slice(0, q);
    const slash = bucketAndPath.indexOf('/');
    if (slash <= 0) return null;

    const bucket = bucketAndPath.slice(0, slash);
    if (!KNOWN_BUCKETS.has(bucket)) {
      reportOnce(`bucket:${bucket}`, `[storage] unknown bucket "${bucket}" in a stored URL; add it to KNOWN_BUCKETS and the storage migration`);
      return null;
    }
    const path = safeDecode(bucketAndPath.slice(slash + 1));
    return path ? { bucket, path } : null;
  }

  // Anything else with a scheme (http, data:, blob:) is not ours.
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return null;
  if (!opts.bucket) return null;

  const q = raw.indexOf('?');
  const path = (q === -1 ? raw : raw.slice(0, q)).replace(/^\/+/, '');
  return path ? { bucket: opts.bucket, path } : null;
}

/**
 * What a value that is NOT one of ours may be handed back as, for an href, a
 * src or window.open: web links, local blob:/data:image previews and app-
 * relative paths. Anything else (`javascript:`, a bare storage path with no
 * bucket) is refused rather than rendered: staff can write these columns, and
 * a `javascript:` link opened from the app would run with the app's origin.
 */
function isSafePassThrough(raw: string): boolean {
  return /^https?:\/\//i.test(raw) || /^blob:/i.test(raw) || /^data:image\//i.test(raw) || /^\/(?!\/)/.test(raw);
}

/**
 * An unescaped '%' in a hand-entered value makes decodeURIComponent throw.
 * Several columns are dual-purpose, and one bad row must not take down the
 * whole batch it happens to be in.
 */
function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Signed URLs are good for an hour; re-signing on every render would issue a
 * request per image per paint. Cached by "bucket/path" for HALF the lifetime,
 * so any URL handed out still has at least 30 minutes left: a link rendered
 * into a page must not expire a minute after the person sees it.
 */
const SIGNED_TTL_SECONDS = 3600;
const CACHE_REUSE_MS = (SIGNED_TTL_SECONDS / 2) * 1000;
const signedCache = new Map<string, { url: string; expiresAt: number }>();

/** Requests in flight, so N components asking for one object issue ONE request. */
const inFlight = new Map<string, Promise<string | null>>();

/**
 * Bumped by clearSignedUrlCache() and by every forced re-sign. A request
 * captures it when it starts and does not cache its result if it has moved on,
 * so a sign in flight during sign-out cannot write the previous user's URL
 * back, and a superseded request cannot overwrite a fresh URL.
 */
let cacheGeneration = 0;

function pruneSignedCache(): void {
  const now = Date.now();
  for (const [k, v] of signedCache) if (v.expiresAt <= now) signedCache.delete(k);
}

function cacheKey(ref: StorageRef): string {
  return `${ref.bucket}/${ref.path}`;
}

/** Drop everything (sign-in/out), or one bucket after a re-upload replaced an object. */
export function clearSignedUrlCache(bucket?: string): void {
  cacheGeneration++;
  if (!bucket) {
    signedCache.clear();
    inFlight.clear();
    return;
  }
  for (const key of [...signedCache.keys()]) {
    if (key.startsWith(`${bucket}/`)) signedCache.delete(key);
  }
  for (const key of [...inFlight.keys()]) {
    if (key.startsWith(`${bucket}/`)) inFlight.delete(key);
  }
}

/**
 * Exchange one stored value for a signed URL.
 *
 * Returns the input unchanged when it is not a storage reference (external
 * links keep working), and null when signing fails. Callers show the file as
 * unavailable — never fall back to the public URL: on a private bucket that is
 * a broken link, and on a public one it is the exposure being removed.
 */
export async function signedUrlFromStored(
  stored: string | null | undefined,
  opts: StorageOptions & { force?: boolean } = {}
): Promise<string | null> {
  const raw = String(stored || '').trim();
  if (!raw) return null;

  const ref = parseStorageRef(raw, opts);
  if (!ref) return isSafePassThrough(raw) ? raw : null; // not ours

  const key = cacheKey(ref);
  if (opts.force) {
    cacheGeneration++;
    signedCache.delete(key);
    inFlight.delete(key);
  } else {
    const hit = signedCache.get(key);
    if (hit && hit.expiresAt > Date.now()) return hit.url;
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const generation = cacheGeneration;
  const request = (async (): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from(ref.bucket)
      .createSignedUrl(ref.path, SIGNED_TTL_SECONDS);

    if (error || !data?.signedUrl) {
      reportOnce(`sign:${ref.bucket}:${error?.message}`, `[storage] could not sign a file in ${ref.bucket}: ${error?.message ?? 'no URL returned'}`);
      return null;
    }

    if (generation === cacheGeneration) {
      pruneSignedCache();
      signedCache.set(key, { url: data.signedUrl, expiresAt: Date.now() + CACHE_REUSE_MS });
    }
    return data.signedUrl;
  })();

  inFlight.set(key, request);
  try {
    return await request;
  } finally {
    // Only retract our own entry — a newer request may have replaced it.
    if (inFlight.get(key) === request) inFlight.delete(key);
  }
}

/**
 * Sign a batch, one request per bucket per 500 files instead of one per file.
 * Keys of the returned map are the ORIGINAL stored values (trimmed), so a
 * caller looks results up by whatever it already holds. Values that are not
 * ours map to themselves; values that failed to sign are absent.
 */
export async function signedUrlsFromStored(
  stored: Array<string | null | undefined>,
  opts: StorageOptions = {}
): Promise<Map<string, string>> {
  const generation = cacheGeneration;
  const out = new Map<string, string>();
  const byBucket = new Map<string, Array<{ original: string; ref: StorageRef }>>();

  for (const value of stored) {
    const raw = String(value || '').trim();
    if (!raw || out.has(raw)) continue;

    const ref = parseStorageRef(raw, opts);
    if (!ref) {
      if (isSafePassThrough(raw)) out.set(raw, raw);
      continue;
    }
    const hit = signedCache.get(cacheKey(ref));
    if (hit && hit.expiresAt > Date.now()) {
      out.set(raw, hit.url);
      continue;
    }
    const list = byBucket.get(ref.bucket) || [];
    list.push({ original: raw, ref });
    byBucket.set(ref.bucket, list);
  }

  // The server rejects more than 1000 paths with a 400 before the handler
  // runs, which would drop the whole bucket's URLs. Chunk well under that.
  const CHUNK = 500;

  for (const [bucket, items] of byBucket) {
    for (let i = 0; i < items.length; i += CHUNK) {
      const slice = items.slice(i, i + CHUNK);

      // Several stored values can point at the same object; ask once per path
      // and fan the result back out.
      const byPath = new Map<string, Array<{ original: string; ref: StorageRef }>>();
      for (const it of slice) {
        const list = byPath.get(it.ref.path) || [];
        list.push(it);
        byPath.set(it.ref.path, list);
      }

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrls([...byPath.keys()], SIGNED_TTL_SECONDS);

      if (error || !data) {
        reportOnce(`batch:${bucket}:${error?.message}`, `[storage] could not sign ${slice.length} file(s) in ${bucket}: ${error?.message ?? 'no data returned'}`);
        continue;
      }

      // Match on the path the server echoes back, never on position: these
      // are driver documents, and an off-by-one would hand one driver's link
      // for another driver's file.
      for (const entry of data) {
        if (!entry?.signedUrl || !entry.path) continue;
        const group = byPath.get(entry.path);
        if (!group) continue;
        for (const item of group) {
          if (generation === cacheGeneration) {
            signedCache.set(cacheKey(item.ref), {
              url: entry.signedUrl,
              expiresAt: Date.now() + CACHE_REUSE_MS,
            });
          }
          out.set(item.original, entry.signedUrl);
        }
      }
    }
  }
  return out;
}

/**
 * Open a stored file in a new tab, signing on demand. For click handlers.
 *
 * The tab is opened BEFORE awaiting: Safari and Firefox block window.open once
 * the call is no longer inside the user gesture. Returns false when the file
 * could not be opened (popup blocked or signing failed) so the caller can warn.
 */
export async function openStoredFile(
  stored: string | null | undefined,
  opts: StorageOptions = {}
): Promise<boolean> {
  const tab = window.open('', '_blank');
  if (tab) {
    try { tab.document.write('Opening…'); } catch { /* cross-origin guard */ }
  }
  try {
    const url = await signedUrlFromStored(stored, opts);
    if (!url) {
      tab?.close();
      return false;
    }
    if (tab) {
      // The tab was opened without noopener (it has to exist before the
      // await); cut the link back to this window before navigating it.
      tab.opener = null;
      tab.location.href = url;
      return true;
    }
    return false;
  } catch (err) {
    console.error('[storage] openStoredFile failed:', err instanceof Error ? err.message : err);
    tab?.close();
    return false;
  }
}

/**
 * The bytes of a stored file, for PDFs, ZIPs and downloads.
 *
 * Our own files are read through a fresh 60-second signature with
 * `cache: 'no-store'`, NOT storage `.download()`: that issues the same plain GET
 * every time, objects carry Cache-Control max-age=3600, and files replaced in
 * place (invoices are upserted at invoices/<n>.pdf, the logo at a fixed key)
 * would come back stale from the browser cache, e.g. the old invoice sent to
 * factoring. External links are fetched. Throws when the file cannot be read,
 * including an error body or an empty file, so a caller can never save an
 * error page as a PDF.
 */
export async function fetchStoredBytes(
  stored: string | null | undefined,
  opts: StorageOptions = {}
): Promise<Blob> {
  const raw = String(stored || '').trim();
  if (!raw) throw new Error('This document has no file attached');

  const ref = parseStorageRef(raw, opts);
  let blob: Blob;
  if (ref) {
    const { data, error } = await supabase.storage.from(ref.bucket).createSignedUrl(ref.path, 60);
    if (error || !data?.signedUrl) throw new Error(error?.message || 'Storage refused the download');
    const response = await fetch(data.signedUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
    blob = await response.blob();
  } else if (/^https?:\/\//i.test(raw)) {
    const response = await fetch(raw);
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
    blob = await response.blob();
  } else {
    throw new Error('Unrecognised file reference');
  }

  if (blob.size === 0) throw new Error('The file is empty');
  return blob;
}
